import React, { useEffect, useState } from 'react';
import { NavBar, Skeleton, PullToRefresh } from 'antd-mobile';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import {DeliveryTaskOrderType} from "../../../delivery/graphql/types/DeliveryTaskOrderType";
import { GET_DELIVERY_POD_ABNORMAL_TYPE_LIST, GetDeliveryPODAbnormalTypeListResponse } from 'pages/delivery/graphql/queries/getDeliveryPODAbnormalTypeList';
import {DeliveringPath, DeliveryDetailPath, DspPath} from "../../../../enums/DeliveryNavigatePathEnum";
import { getDeliveringListPath } from "../../../../utils/deliveryRouteUtils";
import EmptyPodReviewState from "../../../../components/EmptyPodReviewState";
import {PodReviewCard} from "../../../../components/PodReviewCard";
import { isDspManager } from 'utils/auth';
import {getNavBarContainerStyle} from "../../../../utils/deviceUtils";
import {WEB_TEMPORARY_GRAY_ENABLED} from "../../../../utils/grayscaleDriverUtils";

const PageBody: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [sortedOrders, setSortedOrders] = useState<DeliveryTaskOrderType[]>([]);
    // 专用轻接口取数：cache-and-network 先出缓存再后台拉最新，避免连带拉司机全量任务大接口。
    // useQuery 挂载即自动首拉，无需额外的“挂载必拉” useEffect。
    const { data: queryData, loading, refetch } = useQuery<GetDeliveryPODAbnormalTypeListResponse>(
        GET_DELIVERY_POD_ABNORMAL_TYPE_LIST,
        {
            fetchPolicy: 'cache-and-network', // 有网络时拉最新，无网络时用缓存
            errorPolicy: 'all', // 出错时仍保留已返回的 data，避免刷新失败清空列表（与原 store 容错行为一致）
        }
    );
    const data = queryData?.getDeliveryPODAbnormalTypeListForCurrentUser;
    const location = useLocation();
    /* 1. 从详情页返回时拉一次（解决“改完状态回来旧数据”） */
    useEffect(() => {
        if (location.state?.from === 'detail') {
            refetch();
            // 清标记，防止后退再刷
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, refetch, navigate]);
    /* 2. 窗口获得焦点补拉（兜底：切 App 再回来） */
    useEffect(() => {
        const onFocus = () => refetch();
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [refetch]);
    // 恢复页面滚动位置
    useEffect(() => {
        if (!loading && sortedOrders.length > 0) {
            const savedScrollTop = sessionStorage.getItem('driverViolationReviewPageScrollTop');
            if (savedScrollTop) {
                setTimeout(() => {
                    window.scrollTo(0, parseInt(savedScrollTop));
                    sessionStorage.removeItem('driverViolationReviewPageScrollTop');
                }, 200);
            }
        }
    }, [loading, sortedOrders]);
    // 数据处理和排序
    useEffect(() => {
        if (data && data.length > 0) {
            const orders = data as unknown as DeliveryTaskOrderType[];

            const sorted = [...orders].sort((a, b) => {
                /* -------- 原有逻辑 -------- */
                const getRemainingMs = (o: DeliveryTaskOrderType) => {
                    const podUpdateTimeString = o.podUpdateTimeString;
                    if (!podUpdateTimeString) return Infinity;
                    return Number(podUpdateTimeString) * 1000 - Date.now();
                };

                const remainingMsA = getRemainingMs(a);
                const remainingMsB = getRemainingMs(b);

                const isUrgentA = remainingMsA > 0 && remainingMsA < 60 * 60_000 && a.orderWithAddress.order.podReviewStatus !== 4;
                const isUrgentB = remainingMsB > 0 && remainingMsB < 60 * 60_000 && b.orderWithAddress.order.podReviewStatus !== 4;

                /* -------- 新增：已审核统一沉底 -------- */
                const isApprovedA = a.orderWithAddress.order.podReviewStatus === 4;
                const isApprovedB = b.orderWithAddress.order.podReviewStatus === 4;

                // 1. 已审核 vs 未审核 → 已审核放后面
                if (isApprovedA && !isApprovedB) return 1;
                if (!isApprovedA && isApprovedB) return -1;

                // 2. 都是未审核 → 保持原来“紧急置顶 + 剩余时间”逻辑
                if (!isApprovedA && !isApprovedB) {
                    if (isUrgentA && !isUrgentB) return -1;
                    if (!isUrgentA && isUrgentB) return 1;
                    return remainingMsA - remainingMsB;
                }

                // 3. 都是已审核 → 单纯按剩余时间升序（也可再加其他规则）
                return remainingMsA - remainingMsB;
            });
            setSortedOrders(sorted);
        }
    }, [data]);

    // 处理卡片操作
    const handleCardAction = (trackingNumber: string) => {
        // 保存页面滚动位置
        sessionStorage.setItem('driverViolationReviewPageScrollTop', window.scrollY.toString());
        
        // 从 sortedOrders 中找到对应的订单来获取 deliveryTaskOrderId
        const order = sortedOrders.find(o => o.trackingNumber === trackingNumber);
        const deliveryTaskOrderId = order?.id;
        
        console.log('找到的 deliveryTaskOrderId:', deliveryTaskOrderId);

        // 跳转到订单详情页面
        navigate(DeliveryDetailPath.DELIVERY_DELIVERING_ORDER_DETAIL.replace(':trackingNumber', trackingNumber),{
            state:{
                deliveryTaskOrderId: deliveryTaskOrderId,
                trackingNumber: trackingNumber,
                detailType:'pod',
                source: 'driver-violation-review'
            }
        });
    };


    // 仅首屏（尚无数据）显示骨架屏；refetch 刷新时已有数据，不再整页闪骨架屏，
    // 与原 store 版“刷新不改 loading、列表原地更新”的体验保持一致。
    if (loading && !data) {
        return (
            <div className="flex flex-col h-screen">
                <Skeleton.Title animated/>
                <Skeleton.Paragraph lineCount={3} animated/>
                <Skeleton.Title animated/>
                <Skeleton.Paragraph lineCount={3} animated/>
                <Skeleton.Title animated/>
                <Skeleton.Paragraph lineCount={3} animated/>
                <Skeleton.Title animated/>
                <Skeleton.Paragraph lineCount={3} animated/>
            </div>
        );
    }

    const handleRefresh = async () => {
        await refetch();
    };

    // 过滤有效的订单
    const validOrders = sortedOrders.filter(order => order.trackingNumber != null && order.trackingNumber !== '');

    return (
        <div className="bg-gray-50">
            <PullToRefresh onRefresh={handleRefresh}>
                {validOrders.length === 0 ? (
                    <EmptyPodReviewState />
                ) : (
                    <div className="p-4">
                        {/* 顶部提示条：与原生 TTPodTopTipView 一致 */}
                        <div className="flex items-stretch rounded-lg mb-3 overflow-hidden" style={{ backgroundColor: '#FEE2E1' }}>
                            <div className="w-1" style={{ backgroundColor: '#ef4444' }} />
                            <p className="flex-1 py-2 px-2 text-sm font-semibold" style={{ color: '#DF2B34' }}>
                                {t('pages.delivery.podReview.topTip')}
                            </p>
                        </div>
                        {validOrders.map((order) => (
                            <PodReviewCard
                                key={order.trackingNumber!}
                                trackingNumber={order.trackingNumber!}
                                deliveredAt={order.orderWithAddress.order.deliveredAt ? parseInt(order.orderWithAddress.order.deliveredAt, 10) : null}
                                preDeliveredAt={order.orderWithAddress.order.preDeliveredAt ? parseInt(order.orderWithAddress.order.preDeliveredAt, 10) : null}
                                podUpdateTimeString={order.podUpdateTimeString ?? undefined}
                                isAllowUpdatePod={order.isAllowUpdatePod ?? true}
                                podReviewStatus={order.orderWithAddress.order.podReviewStatus}
                                podReviewMessage={order.orderWithAddress.order.podReviewMessage}
                                podReviewReasons={order.orderWithAddress.order.podReviewReasons}
                                deliveredDriverAccountName={order.deliveredDriverAccountName}
                                onAction={handleCardAction}
                            />
                        ))}
                    </div>
                )}
            </PullToRefresh>
        </div>
    );
};

const DriverViolationReviewPage: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const location = useLocation();
    
    // 安全的返回导航逻辑，根据用户角色和来源页面返回不同路径
    const handleBackNavigation = async () => {
        // 检查是否从MapModePage进入
        const fromMapMode = location.state?.from === 'MapModePage';
        
        if (fromMapMode) {
            // 从MapModePage进入，返回到MapModePage，并传递来源信息
            const webTempGrayEnabled = localStorage.getItem(WEB_TEMPORARY_GRAY_ENABLED);

            if (webTempGrayEnabled) {
                // 灰度模式，跳转到临时地图页面
                navigate("/delivery/temporary/map-mode", {
                    state: {
                        from: 'DriverViolationReviewPage'
                    }
                });
            } else {
                // 正常模式，跳转到正式地图页面
                navigate(DeliveringPath.DELIVERY_MAP_MODE, {
                    state: {
                        from: 'DriverViolationReviewPage'
                    }
                });
            }
        } else {
            // 检查是否有明确的来源路径
            const fromPath = location.state?.from;
            
            if (fromPath) {
                // 如果有明确的来源路径，直接返回到该路径
                navigate(fromPath);
            } else {
                // 根据用户角色决定返回路径
                if (isDspManager()) {
                    navigate(DspPath.DELIVERY_DSP_HOME);
                } else {
                    const deliveringPath = await getDeliveringListPath();
                    navigate(deliveringPath);
                }
            }
        }
    };
    
    return (
        <div className="bg-white">
            <div {...getNavBarContainerStyle()} className="fixed top-0 left-0 right-0 z-20 bg-white shadow-sm">
                <NavBar
                    onBack={() => {
                        void handleBackNavigation();
                    }}
                    backIcon={<ArrowLeftOutlined/>}
                >
                    {isDspManager() ? t('pages.delivery.podReview.driverViolationReview') : t('pages.delivery.podReview.issueResolution')}
                </NavBar>
            </div>
            <div className="pt-16">
                <PageBody/>
            </div>
        </div>
    );
};

export default DriverViolationReviewPage;
